import { ApiEndpoint, EndpointParameter, EndpointResponse, ExpandedField, HttpMethod, ParameterType } from './endpoint.interface';
import { APISpec, Operation, Parameter, PathItem, RequestBody, Response, Responses, Schema } from './api-spec.interface';
import { generateSchemaExample, getSchemaDataType } from './schema-introspection.utils';
import { expandSchemaToFields, shouldExpandSchema } from './schema-fields.utils';

/**
 * Parse all endpoints from the OpenAPI specification
 * @param api - The resolved OpenAPI specification
 * @returns Array of parsed API endpoints
 */
export function parseEndpoints(api: APISpec): ApiEndpoint[] {
  const endpoints: ApiEndpoint[] = [];

  Object.entries(api.paths || {}).forEach(([path, pathItem]: [string, PathItem]) => {
    Object.entries(pathItem).forEach(([method, operation]) => {
      if (isValidHttpMethod(method) && operation && typeof operation === 'object') {
        try {
          const endpoint = createEndpoint(path, method as HttpMethod, operation as Operation, api);
          endpoints.push(endpoint);
        } catch (error) {
          console.error(`Error parsing endpoint ${method.toUpperCase()} ${path}:`, error);
        }
      }
    });
  });
  return endpoints;
}

/**
 * Create an endpoint object from path and operation data
 * @param path - The URL path
 * @param method - The HTTP method
 * @param operation - The operation definition
 * @param api - The full OpenAPI specification
 * @returns Parsed endpoint object
 */
export function createEndpoint(path: string, method: HttpMethod, operation: Operation, api: APISpec): ApiEndpoint {
  const id = generateEndpointId(path, method);

  return {
    id,
    path,
    method,
    operationId: operation.operationId,
    summary: operation.summary,
    description: operation.description,
    tags: operation.tags || ['Other'],
    parameters: parseParameters(operation, path),
    responses: parseResponses(operation.responses),
    security: operation.security || [],
    produces: getProducesFromOperation(operation, api),
    consumes: getConsumesFromOperation(operation, api),
  };
}

/**
 * Parse operation parameters into structured format
 * @param operation - The operation object
 * @param path - The endpoint path (for context)
 * @returns Array of parsed parameters with examples
 */
export function parseParameters(operation: Operation, _: string): EndpointParameter[] {
  const parameters: EndpointParameter[] = [];

  // Parse path/query/header parameters
  if (operation.parameters && Array.isArray(operation.parameters)) {
    operation.parameters.forEach((param: Parameter) => {
      const parsedParam = parseParameter(param);
      if (parsedParam) {
        parameters.push(parsedParam);
      }
    });
  }

  // Parse request body (OpenAPI 3.0)
  if (operation.requestBody) {
    const bodyParam = parseRequestBody(operation.requestBody, operation.operationId);
    if (bodyParam) {
      parameters.push(bodyParam);
    }
  }

  return parameters;
}

/**
 * Parse a single parameter
 * @param param - The parameter definition
 * @returns Parsed parameter
 */
export function parseParameter(param: Parameter): EndpointParameter | null {
  if (!param.name) {
    return null;
  }

  const schema: Schema = param.schema || {};

  return {
    name: param.name,
    type: param.in as ParameterType,
    dataType: getSchemaDataType(schema),
    required: param.required === true,
    description: param.description,
    schema: schema,
    nullable: schema.nullable,
    example: generateSchemaExample(schema, param.name),
    defaultValue: schema.default,
  };
}

/**
 * Parse request body into parameter format
 * @param requestBody - The request body definition
 * @param operationId - Operation ID for context
 * @returns Parsed body parameter or null
 */
export function parseRequestBody(requestBody: RequestBody, operationId?: string): EndpointParameter | null {
  // Get the first content type (prefer application/json)
  const contentTypes = Object.keys(requestBody.content || {});
  const contentType = contentTypes.find(ct => ct.includes('json')) || contentTypes[0];

  if (!contentType) {
    return null;
  }

  const mediaType = requestBody.content[contentType];
  const schema = mediaType.schema;

  if (!schema) {
    return null;
  }

  // Extract the parameter name
  const paramName = requestBody['x-name'] || inferBodyParamName(operationId) || 'body';

  // For body parameters with object schemas, expand the fields
  let expandedFields: ExpandedField[] | undefined;
  if (shouldExpandSchema(schema)) {
    expandedFields = expandSchemaToFields(schema);
  }

  return {
    name: paramName,
    type: 'body',
    dataType: getSchemaDataType(schema),
    required: requestBody.required || false,
    description: requestBody.description || schema.description,
    schema: schema,
    nullable: schema.nullable,
    example: expandedFields ? null : generateSchemaExample(schema, paramName),
    defaultValue: schema.default,
    expandedFields: expandedFields,
  };
}

/**
 * Infer body parameter name from operation ID
 * @param operationId - The operation ID
 * @returns Inferred parameter name
 */
export function inferBodyParamName(operationId?: string): string | null {
  if (!operationId) return null;

  const baseName = operationId.charAt(0).toLowerCase() + operationId.slice(1);
  return `${baseName}Request`;
}

/**
 * Parse operation responses
 * @param responses - Response definitions from OpenAPI
 * @returns Array of parsed responses
 */
export function parseResponses(responses: Responses): EndpointResponse[] {
  if (!responses) return [];

  return Object.entries(responses).map(([statusCode, response]: [string, Response | undefined]) => {
    let schema: Schema | undefined;
    let contentType: string | null = null;

    // OpenAPI 3.0 style with content
    if (response?.content) {
      const contentTypes = Object.keys(response.content);
      contentType = contentTypes.find(ct => ct.includes('json')) || contentTypes[0];

      if (contentType && response.content[contentType]) {
        schema = response.content[contentType].schema;
      }
    }

    return {
      statusCode,
      description: response?.description || '',
      schema: schema,
      contentType: contentType,
      nullable: schema?.nullable,
    };
  });
}

/**
 * Get produces array from operation
 * @param operation - The operation object
 * @param api - The full API spec for fallback
 * @returns Array of produced content types
 */
export function getProducesFromOperation(operation: Operation, api: APISpec): string[] | undefined {
  // OpenAPI 3.0 - from responses
  if (operation.responses) {
    const produces = new Set<string>();

    Object.values(operation.responses).forEach((response: Response | undefined) => {
      if (response?.content) {
        Object.keys(response.content).forEach(contentType => {
          produces.add(contentType);
        });
      }
    });

    if (produces.size > 0) {
      return Array.from(produces);
    }
  }

  // Swagger 2.0 fallback
  return operation.produces || api.produces;
}

/**
 * Get consumes array from operation
 * @param operation - The operation object
 * @param api - The full API spec for fallback
 * @returns Array of consumed content types
 */
export function getConsumesFromOperation(operation: Operation, api: APISpec): string[] | undefined {
  // OpenAPI 3.0 - from requestBody
  if (operation.requestBody?.content) {
    return Object.keys(operation.requestBody.content);
  }

  // Swagger 2.0 fallback
  return operation.consumes || api.consumes;
}

/**
 * Generate unique endpoint ID
 * @param path - The endpoint path
 * @param method - The HTTP method
 * @returns Generated endpoint ID
 */
export function generateEndpointId(path: string, method: string): string {
  const cleanPath = path
    .replace(/^\//, '')
    .replace(/\{([^}]+)\}/g, '$1')
    .replace(/[\\/\\-]/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return `${cleanPath}-${method.toLowerCase()}`;
}

/**
 * Check if a string is a valid HTTP method
 * @param method - The method string to check
 * @returns Whether the method is valid
 */
export function isValidHttpMethod(method: string): boolean {
  const validMethods = ['get', 'post', 'put', 'delete', 'patch', 'head', 'options'];
  return validMethods.includes(method.toLowerCase());
}
